// components/categories/CTAButton.tsx
'use client';

import React, { useRef } from 'react';
import Link from 'next/link';
import gsap from 'gsap';
import { useGSAP } from '@gsap/react';
import { ArrowRight } from 'lucide-react';
import { CornerBracket } from './CornerBracket';

interface CTAButtonProps {
  label?: string;
  href?: string;
}

export const CTAButton: React.FC<CTAButtonProps> = ({
  label = 'View All Products',
  href = '/products',
}) => {
  const wrapRef = useRef<HTMLDivElement | null>(null);
  const btnRef = useRef<HTMLAnchorElement | null>(null);
  const fillRef = useRef<HTMLSpanElement | null>(null);

  const quickX = useRef<gsap.QuickToFunc | null>(null);
  const quickY = useRef<gsap.QuickToFunc | null>(null);

  useGSAP(() => {
    if (!btnRef.current) return;
    quickX.current = gsap.quickTo(btnRef.current, 'x', { duration: 0.45, ease: 'power3.out' });
    quickY.current = gsap.quickTo(btnRef.current, 'y', { duration: 0.45, ease: 'power3.out' });
  }, { scope: wrapRef });

  const handleMouseEnter = () => {
    gsap.to(fillRef.current, {
      scaleX: 1,
      duration: 0.45,
      ease: 'power3.out',
      transformOrigin: 'left center',
    });
  };

  const handleMouseMove = (e: React.MouseEvent<HTMLAnchorElement>) => {
    const rect = btnRef.current?.getBoundingClientRect();
    if (!rect) return;
    quickX.current?.((e.clientX - rect.left - rect.width / 2) * 0.22);
    quickY.current?.((e.clientY - rect.top - rect.height / 2) * 0.38);
  };

  const handleMouseLeave = () => {
    gsap.to(fillRef.current, {
      scaleX: 0,
      duration: 0.35,
      ease: 'power3.in',
      transformOrigin: 'right center',
    });
    quickX.current?.(0);
    quickY.current?.(0);
  };

  return (
    <div ref={wrapRef} className="flex justify-center mt-2">
      <Link
        ref={btnRef}
        href={href}
        onMouseEnter={handleMouseEnter}
        onMouseMove={handleMouseMove}
        onMouseLeave={handleMouseLeave}
        aria-label={label}
        className="group relative inline-flex items-center gap-3 px-10 py-4 will-change-transform overflow-hidden"
      >
        {/* Outer border */}
        <span aria-hidden className="absolute inset-0 border border-[#1C1B19]" />

        {/* Corner brackets — appear on hover */}
        {(['tl', 'tr', 'bl', 'br'] as const).map((c) => (
          <CornerBracket
            key={c}
            corner={c}
            size={8}
            thickness={1.5}
            color="#B08D57"
            className="opacity-0 group-hover:opacity-100 transition-opacity duration-300"
          />
        ))}

        {/* Fill wipe */}
        <span
          ref={fillRef}
          aria-hidden
          className="absolute inset-0 bg-[#1C1B19] pointer-events-none"
          style={{ transform: 'scaleX(0)', transformOrigin: 'left center' }}
        />

        {/* Text */}
        <span className="relative z-10 text-[11px] tracking-[0.2em] uppercase font-semibold text-[#1C1B19] group-hover:text-[#FEFDFB] transition-colors duration-200">
          {label}
        </span>

        {/* Arrow */}
        <ArrowRight
          size={14}
          strokeWidth={2}
          className="relative z-10 text-[#1C1B19] group-hover:text-[#FEFDFB] group-hover:translate-x-1 transition-all duration-300"
        />
      </Link>
    </div>
  );
};